/**
 * The ciclo attivo of a commessa (2.03, decisions of 16-17/10/2026): the rate
 * planned for invoicing, each with two dates (emissione and incasso), the
 * issued invoices divided among commesse, and where each rata stands. Pure
 * rules: the API stores, these decide.
 */
import { z } from 'zod';
import { addDays, daysInMonth, isoDate, parseIsoDate } from './dates.js';
import { fromCents, toCents, toThousandths } from './money.js';
import { editionsSchema, licenceLinesSchema, quoteLineSchema } from './quotes.js';
import { idSchema, isoDateSchema, optionalText, requiredText } from './validation.js';
/** «A data fissa» (anticipo, canoni) or «all’erogazione», with the via libera. */
export const INSTALLMENT_KINDS = ['data_fissa', 'erogazione'];
export const INSTALLMENT_KIND_LABELS = {
    data_fissa: 'A data fissa',
    erogazione: 'All’erogazione',
};
export const INSTALLMENT_STATES = [
    'prevista',
    'fatturabile',
    'parziale',
    'fatturata',
    'incassata',
];
export const INSTALLMENT_STATE_LABELS = {
    prevista: 'Prevista',
    fatturabile: 'Fatturabile',
    parziale: 'Fatturata in parte',
    fatturata: 'Fatturata',
    incassata: 'Incassata',
};
/** Days after which a rata fatturabile without its invoice raises a notice (Organizzazione, rule 13). */
export const INSTALLMENT_REMINDER_DAYS = 7;
/**
 * The due date of a document from its date and terms: months first (keeping
 * the day within the month), then days, then the end of that month when
 * asked. «30 gg fine mese» (1 month, end of month) from 15/10 is 30/11.
 */
export function dueFromTerms(date, terms) {
    const start = parseIsoDate(date);
    const months = start.getUTCMonth() + terms.months;
    const year = start.getUTCFullYear() + Math.floor(months / 12);
    const month = (((months % 12) + 12) % 12) + 1;
    const day = Math.min(start.getUTCDate(), daysInMonth(year, month));
    let due = addDays(isoDate(year, month, day), terms.days);
    if (terms.endOfMonth) {
        const end = parseIsoDate(due);
        const y = end.getUTCFullYear();
        const m = end.getUTCMonth() + 1;
        due = isoDate(y, m, daysInMonth(y, m));
    }
    return due;
}
/** The terms read from a row of the table, missing values as zero. */
export function paymentTermsOf(values) {
    return {
        months: Number(values.months ?? 0) || 0,
        days: Number(values.days ?? 0) || 0,
        endOfMonth: values.endOfMonth === true,
    };
}
/**
 * The rate of an offer from its payment plan (percent or amount of the
 * imponibile): the last one takes the rounding, so that they add up to the
 * offer. Without a plan, one rata for the whole offer.
 */
export function installmentsFromOffer(net, plan, fallback) {
    const total = toCents(net);
    if (plan.length === 0)
        return [{ description: fallback, amount: net }];
    let left = total;
    return plan.map((item, index) => {
        const cents = index === plan.length - 1
            ? left
            : item.amount !== null
                ? toCents(item.amount)
                : Math.round((total * Number(item.percent ?? 0)) / 100);
        left -= cents;
        return { description: item.description, amount: fromCents(cents) };
    });
}
/** The VAT of an imponibile at a rate (22 = 22 %), to the cent. */
export function vatOf(amount, rate) {
    return fromCents(Math.round((toCents(amount) * Number(rate)) / 100));
}
/**
 * Where a rata stands. Invoiced is the imponibile of the issued invoices
 * linked to it; collected comes from their settlements. A rata «a data fissa»
 * becomes fatturabile on its date, one «all’erogazione» with the via libera.
 */
export function installmentState(input) {
    const amount = toCents(input.amount);
    const invoiced = toCents(input.invoiced);
    if (invoiced !== 0 && Math.abs(invoiced) >= Math.abs(amount)) {
        return { state: input.collected ? 'incassata' : 'fatturata', lateDays: 0 };
    }
    const since = input.kind === 'data_fissa' ? input.issueOn : input.releasedOn;
    if (since === null || since > input.today) {
        return { state: invoiced !== 0 ? 'parziale' : 'prevista', lateDays: 0 };
    }
    const days = Math.floor((parseIsoDate(input.today).getTime() - parseIsoDate(since).getTime()) / 86_400_000);
    return {
        state: invoiced !== 0 ? 'parziale' : 'fatturabile',
        lateDays: days > INSTALLMENT_REMINDER_DAYS ? days : 0,
    };
}
/* --- Requests ------------------------------------------------------------------ */
const money = z
    .string()
    .trim()
    .regex(/^-?\d+(\.\d{1,2})?$/, { message: 'Importo non valido' });
/**
 * A line of a rata composed like a line of the preventivo (2.50, 28/10/2026):
 * type, sub-type, editions, quantity, unit, price, discount. The VAT is the
 * one of the rata.
 */
export const jobInstallmentLineSchema = quoteLineSchema.omit({ vatCode: true, recurrence: true });
export const jobInstallmentSchema = z
    .object({
    description: requiredText(200),
    /**
     * The lines of a rata «dalle tipologie» (2.50); null: a rata «libera», its
     * amount written. With lines the amount is their sum.
     */
    lines: z.array(jobInstallmentLineSchema).max(100).nullable().default(null),
    /** The licences of the rata, worked out on the price list as in the preventivo (2.50). */
    licences: licenceLinesSchema.nullable().default(null),
    /** A rata «libera» that is all a spesa (2.52); with lines, each line says it. */
    isExpense: z.boolean().default(false),
    kind: z.enum(INSTALLMENT_KINDS),
    /** «Si fattura a» of this rata; null: the one of the commessa. */
    billingClientId: idSchema.nullable().default(null),
    issueOn: isoDateSchema,
    /** Null: worked out from the terms. */
    collectOn: isoDateSchema.nullable().default(null),
    termsCode: optionalText(30),
    /** Written for a rata «libera»; worked out from the lines otherwise. */
    amount: money.nullable().default(null),
    vatRate: money.default('22'),
    /** The client pays only the imponibile (2.05); null: as its last invoice. */
    splitPayment: z.boolean().nullable().default(null),
    /** Scadenzario (3.25): a rata written by hand, contracted with the client; one from an offer is always certain. */
    contracted: z.boolean().default(false),
})
    .superRefine((body, context) => {
    if (body.collectOn && body.collectOn < body.issueOn) {
        context.addIssue({
            code: 'custom',
            path: ['collectOn'],
            message: 'L’incasso viene dopo l’emissione',
        });
    }
    const composed = body.lines !== null || body.licences !== null;
    if (composed && !body.lines?.length && !body.licences) {
        context.addIssue({ code: 'custom', path: ['lines'], message: 'Aggiungi almeno una riga' });
    }
    if (!composed && (body.amount === null || Number(body.amount) === 0)) {
        context.addIssue({ code: 'custom', path: ['amount'], message: 'Importo a zero' });
    }
});
/** The division of an invoice among commesse, all at once. */
export const allocationsSchema = z.object({
    allocations: z
        .array(z.object({
        jobId: idSchema,
        /** The rata of an issued invoice. */
        installmentId: idSchema.nullable().default(null),
        /** The cost of a received invoice (2.04). */
        costId: idSchema.nullable().default(null),
        /** The line of the invoice (2.44); null: a part of the whole invoice. */
        linePosition: z.number().int().min(0).nullable().default(null),
        amount: money,
    }))
        .max(500),
});
/**
 * The most of the imponibile that may lie outside the lines and still follow
 * them (2.56): beyond it the lines are taken as not all read.
 */
export const LINE_REST_LIMIT = 0.1;
/**
 * The lines of an invoice with their share of the imponibile outside the lines
 * (2.56, 29/10/2026): the cassa previdenziale (rivalsa INPS 4 %) is in the
 * imponibile but not a line of the electronic invoice. It follows the lines in
 * proportion, so that all the lines given is all the invoice given: each line
 * rounded, the last one takes what is left. «base» is the line as read,
 * «rest» its share; the same rounding as migration 0096.
 */
export function linesWithRest(taxable, lines) {
    const total = lines.reduce((sum, line) => sum + toCents(line.amount), 0);
    const whole = toCents(taxable);
    const gap = whole - total;
    const follows = lines.length > 0 &&
        total !== 0 &&
        gap !== 0 &&
        Math.sign(whole) === Math.sign(total) &&
        Math.abs(gap) <= Math.abs(total) * LINE_REST_LIMIT;
    if (!follows)
        return lines.map((line) => ({ ...line, base: line.amount, rest: '0.00' }));
    const last = Math.max(...lines.map((line) => line.position));
    const scaled = (cents) => {
        const exact = (cents * whole) / total;
        return Math.sign(exact) * Math.round(Math.abs(exact));
    };
    const others = lines
        .filter((line) => line.position !== last)
        .reduce((sum, line) => sum + scaled(toCents(line.amount)), 0);
    return lines.map((line) => {
        const base = toCents(line.amount);
        const gross = line.position === last ? whole - others : scaled(base);
        return { ...line, amount: fromCents(gross), base: line.amount, rest: fromCents(gross - base) };
    });
}
/**
 * Why a division of an invoice cannot be saved, or null (2.44, 27/10/2026): a
 * part of a line has the sign of the line and the parts of a line never exceed
 * it; a part of the whole invoice has the sign of the invoice; all the parts
 * never exceed the imponibile. What stays free is allowed.
 */
export function divisionProblem(input) {
    const byLine = new Map();
    let total = 0;
    for (const part of input.parts) {
        const cents = toCents(part.amount);
        const line = part.linePosition === null
            ? null
            : input.lines.find((item) => item.position === part.linePosition);
        if (part.linePosition !== null && !line) {
            return `La riga ${part.linePosition + 1} non è più nella fattura`;
        }
        const of = toCents(line ? line.amount : input.taxable);
        if (cents === 0 || Math.sign(cents) !== (of < 0 ? -1 : 1)) {
            return line
                ? `Ogni parte della riga ${part.linePosition + 1} ha il segno della riga e non è zero`
                : 'Ogni parte ha il segno della fattura e non è zero';
        }
        if (part.linePosition !== null) {
            byLine.set(part.linePosition, (byLine.get(part.linePosition) ?? 0) + cents);
        }
        total += cents;
    }
    for (const [position, cents] of byLine) {
        const line = input.lines.find((item) => item.position === position);
        if (Math.abs(cents) > Math.abs(toCents(line.amount))) {
            return `Le parti della riga ${position + 1} (${fromCents(cents)} €) superano la riga (${line.amount} €)`;
        }
    }
    if (Math.abs(total) > Math.abs(toCents(input.taxable))) {
        return `Le parti (${fromCents(total)} €) superano l’imponibile della fattura (${input.taxable} €)`;
    }
    return null;
}
/**
 * Whether a part given from a line is to review: Gestify changed the line, or
 * the line is gone, after the part was given (2.44).
 */
export function lineChanged(part, lines) {
    if (part.linePosition === null)
        return false;
    const line = lines.find((item) => item.position === part.linePosition);
    return (!line ||
        line.description !== part.lineDescription ||
        toCents(line.amount) !== toCents(part.lineAmount ?? '0'));
}
/** Link (or unlink, with null) a pro-forma to a rata or a cost. */
export const proformaLinkSchema = z.object({ proformaId: idSchema.nullable() });
/* --- Fatture da abbinare (2.56) ----------------------------------------------- */
/**
 * An invoice is «probabile» for a commessa when what is left of it fits in
 * what the rate or the costs of its subject still wait for, with this margin
 * (2.56): the cassa and the roundings.
 */
export const MATCH_FIT_MARGIN = 0.05;
export const invoiceMatchQuerySchema = z.object({
    direction: z.enum(['emessa', 'ricevuta']),
    /** One subject, also one with nothing on the commessa («Altro fornitore…»). */
    subjectId: idSchema.optional(),
    /** «probabili»: those of this commessa; «tutte»: all those of the subjects not yet all given. */
    scope: z.enum(['probabili', 'tutte']).default('probabili'),
});
/** «Abbina» (2.56): a part of an invoice onto a rata, a cost, or the commessa without one. */
export const invoiceMatchSchema = z.object({
    direction: z.enum(['emessa', 'ricevuta']),
    invoiceId: idSchema,
    /** The rata of an issued invoice, the cost of a received one; null: none. */
    itemId: idSchema.nullable().default(null),
    amount: money,
});
/* --- Imputazione guidata (2.47) ---------------------------------------------- */
/**
 * The rata or cost to propose for a part of an invoice (2.47, 28/10/2026),
 * instead of leaving it «senza rata»: among those not yet fully invoiced (of the
 * supplier of the invoice first, for a cost), the one whose rest is nearest to
 * the amount of the part; on a tie, the earliest. Null when none is open.
 */
export function proposedItem(items, amount) {
    const restOf = (item) => toCents(item.amount) - toCents(item.invoiced);
    const open = items.filter((item) => {
        const rest = restOf(item);
        return rest !== 0 && Math.sign(rest) === Math.sign(toCents(item.amount));
    });
    const preferred = open.some((item) => item.sameSubject)
        ? open.filter((item) => item.sameSubject)
        : open;
    const wanted = toCents(amount);
    const [best] = [...preferred].sort((a, b) => Math.abs(restOf(a) - wanted) - Math.abs(restOf(b) - wanted) || a.date.localeCompare(b.date));
    return best?.id ?? null;
}
/** Where an invoice stands with the commesse (2.47), for the filter of the Fatture. */
export const JOB_INVOICE_STATUSES = ['da_imputare', 'parziale', 'imputata', 'esclusa'];
export const JOB_INVOICE_STATUS_LABELS = {
    da_imputare: 'Da imputare',
    parziale: 'Imputate in parte',
    imputata: 'Imputate',
    esclusa: 'Non riguardano commesse',
};
/**
 * «Da imputare» counts the invoices from this many days before the first
 * commessa (28/10/2026): the older ones are history, not work to do.
 */
export const JOB_INVOICE_LOOKBACK_DAYS = 30;
export function jobInvoiceStatus(input) {
    if (input.excluded)
        return 'esclusa';
    const allocated = toCents(input.allocated);
    if (allocated === 0)
        return 'da_imputare';
    return allocated === toCents(input.taxable) ? 'imputata' : 'parziale';
}
/** «Non riguarda commesse» on an invoice (2.47), with an optional note. */
export const invoiceExclusionSchema = z.object({ note: optionalText(500) });
/* --- Ciclo passivo (2.04) --------------------------------------------------------- */
export const COST_STATES = ['previsto', 'parziale', 'consuntivato', 'pagato'];
export const COST_STATE_LABELS = {
    previsto: 'Previsto',
    parziale: 'Consuntivato in parte',
    consuntivato: 'Consuntivato',
    pagato: 'Pagato',
};
/**
 * Where a cost stands: previsto until a received invoice is given to it,
 * consuntivato when the invoices cover it, pagato when they are all paid.
 * Amounts and dates change only while it is not consuntivato (16/10/2026).
 * A cost with a saldo (2.28) is consuntivato with the invoices it has.
 */
export function costState(input) {
    const amount = Math.abs(toCents(input.amount));
    const invoiced = Math.abs(toCents(input.invoiced));
    if (invoiced === 0)
        return 'previsto';
    if (invoiced < amount && !input.settled)
        return 'parziale';
    return input.paid ? 'pagato' : 'consuntivato';
}
/**
 * A cost «dal listino passività» (2.51, 28/10/2026): a voice of the table
 * Costi, its editions, quantity and unit cost (proposed by the voice, always
 * writable). The amount is editions × quantity × unit cost.
 */
export const jobCostVoiceSchema = z.object({
    /** The voice of the table Costi; null (2.54): none, quantity × cost written with its unit. */
    code: z.string().trim().min(1).max(30).nullable().default(null),
    /** The unit of a cost without a voice (2.54); a voice brings its own. */
    unitCode: z.string().trim().min(1).max(30).nullable().default(null),
    editions: editionsSchema.default(1),
    quantity: z
        .union([z.string(), z.number()])
        .transform((value) => (toThousandths(value) / 1000).toFixed(3))
        .refine((value) => Number(value) > 0, { message: 'La quantità deve essere maggiore di zero' }),
    unitCost: money.refine((value) => Number(value) >= 0, {
        message: 'Il costo non può essere negativo',
    }),
});
export const jobCostSchema = z
    .object({
    description: requiredText(200),
    /** The voice of the listino passività (2.51); null: a cost «libero», its amount written. */
    voice: jobCostVoiceSchema.nullable().default(null),
    /** A spesa (2.52): trasferte, vitto, rimborsi; out of the margin, checked on its own. */
    isExpense: z.boolean().default(false),
    /**
     * Subjects with the role Fornitore, in order (2.28): the first gives the
     * terms of payment; empty while not chosen.
     */
    supplierIds: z.array(idSchema).max(10).default([]),
    receiveOn: isoDateSchema,
    /** Null: worked out from the terms. */
    payOn: isoDateSchema.nullable().default(null),
    termsCode: optionalText(30),
    /** Written for a cost «libero»; worked out from the voice otherwise. */
    amount: money.nullable().default(null),
    vatRate: money.default('22'),
    /** Ritenuta d’acconto in percent of the imponibile (2.05); null: as the last invoice of the supplier. */
    withholdingRate: money.nullable().default(null),
    /** Scadenzario (3.25): contracted with the supplier, certain in the schedule. */
    contracted: z.boolean().default(false),
    /** Data fissa, or all’erogazione: payable once released with the via libera. */
    kind: z.enum(INSTALLMENT_KINDS).default('data_fissa'),
})
    .superRefine((body, context) => {
    if (body.payOn && body.payOn < body.receiveOn) {
        context.addIssue({
            code: 'custom',
            path: ['payOn'],
            message: 'Il pagamento viene dopo il ricevimento',
        });
    }
    if (body.voice === null && (body.amount === null || Number(body.amount) === 0)) {
        context.addIssue({ code: 'custom', path: ['amount'], message: 'Importo a zero' });
    }
    if (new Set(body.supplierIds).size !== body.supplierIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['supplierIds'],
            message: 'Un fornitore è scelto due volte',
        });
    }
});
/**
 * Received invoices given to a cost from the ciclo passivo (2.28): each for a
 * part of its imponibile still free, or of the part already on the same
 * commessa without a cost. With «settle» the cost gets its saldo.
 */
export const costInvoicesSchema = z.object({
    items: z
        .array(z.object({ invoiceId: idSchema, amount: money }))
        .min(1)
        .max(20),
    settle: z.boolean().default(false),
    /** The rest of the same invoices on other costs of the commessa (2.55). */
    others: z
        .array(z.object({ costId: idSchema, invoiceId: idSchema, amount: money }))
        .max(20)
        .default([]),
});
/** A rata or a cost one place up or down (2.55). */
export const rowMoveSchema = z.object({ direction: z.enum(['up', 'down']) });
export const costSettleSchema = z.object({ settled: z.boolean() });
//# sourceMappingURL=job-cycles.js.map